/**
 * Coordinates out of a payload whose shape we do not know. PURE.
 *
 * TOLERANT BY NECESSITY, NOT BY PREFERENCE. No `map_points_v2` response has ever been
 * captured — `docs/samples/` has no file for it — and `location`, the other
 * coordinate-bearing endpoint, has only ever returned null. So the field names here are
 * a GUESS across the spellings this vendor uses elsewhere, and the caller is expected to
 * say so when nothing is found rather than draw an empty box.
 *
 * Two spellings are tried because the vendor demonstrably uses both: separate keys in
 * most payloads, and a comma string for `location.coordinate`, which the API reference
 * documents as `"x,y,angle"`.
 *
 * When a real sample turns up, this narrows to what it actually sends.
 */
export interface Pt {
  x: number;
  y: number;
  label?: string;
  /** The vendor's own `type`, e.g. `charge`, `zone_start_pose`. Shown on hover beside
   *  the name, because a point's KIND is most of what identifies it on a map. */
  kind?: string;
  /**
   * Which floor this point is on, as the vendor wrote it.
   *
   * KEPT AS A STRING, never coerced to a number. `floorInfo` goes back to the vendor
   * verbatim in `map_fetch`, and "1" and 1 are not interchangeable in a query string —
   * round-tripping a floor through Number() is how a working floor id becomes one the
   * vendor does not recognise.
   */
  floor?: string;
}

export function readPoints(data: unknown): Pt[] {
  const out: Pt[] = [];
  const num = (v: unknown): number | null => {
    const n = typeof v === "string" ? Number(v) : v;
    return typeof n === "number" && Number.isFinite(n) ? n : null;
  };
  const push = (x: number, y: number, o: Record<string, unknown>) => {
    const label = ["name", "pointName", "targetName", "areaName"]
      .map((k) => o[k]).find((v) => typeof v === "string" && v.trim());
    const floorRaw = ["floor", "floorInfo", "floorName"]
      .map((k) => o[k]).find((v) => typeof v === "string" || typeof v === "number");
    const kind = o.type;
    out.push({
      x, y,
      label: label as string | undefined,
      kind: typeof kind === "string" && kind.trim() ? kind : undefined,
      floor: floorRaw === undefined ? undefined : String(floorRaw),
    });
  };
  const walk = (node: unknown, seen: Set<unknown>) => {
    if (!node || typeof node !== "object" || seen.has(node)) return;
    seen.add(node);
    if (Array.isArray(node)) { for (const n of node) walk(n, seen); return; }
    const o = node as Record<string, unknown>;
    // Spelling 1: separate keys, in any of the shapes seen across this vendor's docs.
    for (const [kx, ky] of [["x", "y"], ["positionX", "positionY"], ["px", "py"],
                            ["originX", "originY"]] as const) {
      const x = num(o[kx]);
      const y = num(o[ky]);
      if (x !== null && y !== null) { push(x, y, o); break; }
    }
    // Spelling 2: a comma string, which is what `location` documents for `coordinate`
    // ("x,y,angle"). If the points endpoint follows the same house style, this catches it.
    for (const k of ["coordinate", "position", "pose"]) {
      const v = o[k];
      if (typeof v !== "string") continue;
      const bits = v.split(",").map((b) => Number(b.trim()));
      if (bits.length >= 2 && bits.slice(0, 2).every(Number.isFinite)) {
        push(bits[0], bits[1], o);
      }
    }
    for (const v of Object.values(o)) walk(v, seen);
  };
  walk(data, new Set());
  return out;
}



/**
 * The distinct floors a set of points mentions, in a stable order.
 *
 * This is where the floor dropdown comes from. `scene_list` does not carry floors and
 * `map_fetch` requires one, so the only way to learn the valid ids without guessing is
 * to read them off the points the scene actually has — which also means a floor offered
 * here is one the vendor has already told us about, rather than one we invented.
 *
 * Sorted numerically when they all look like numbers, so "2" comes before "10"; and
 * lexically otherwise, because a floor called "B1" has no numeric order.
 */
export function floorsOf(points: Pt[]): string[] {
  const out = [...new Set(points.map((p) => p.floor).filter((f): f is string => !!f))];
  const allNumeric = out.every((f) => f.trim() !== "" && Number.isFinite(Number(f)));
  return allNumeric ? out.sort((a, b) => Number(a) - Number(b)) : out.sort();
}


export interface Area { id: string; name: string; floor?: string }

/**
 * The named cleaning areas a site has, per floor.
 *
 * THERE IS NO GEOMETRY. `area_list` returns `areaIdList` and `areaNameList` — two
 * parallel arrays of opaque ids and labels — and nothing else: no polygon, no bounding
 * box, no centre point. Verified against the live sample
 * (`docs/samples/clean_areas_*.json`), whose entity keys are exactly `storeId`,
 * `robotSn`, `mapId`, `floor`, `areaIdList`, `areaNameList`.
 *
 * So regions can be LISTED but not DRAWN, and a caller must say which it is doing.
 * Inventing a shape for them — a grid of boxes, a label at the map's centre — would put
 * something on the map that the vendor never said was there.
 *
 * The two arrays are zipped by position, which is the vendor's own convention and the
 * only link between an id and its name. A length mismatch means the pairing is
 * unreliable, so the shorter length wins rather than pairing a name with `undefined`.
 */
export function readAreas(data: unknown, floor?: string | null): Area[] {
  const out: Area[] = [];
  const seen = new Set<unknown>();
  const walk = (node: unknown) => {
    if (!node || typeof node !== "object" || seen.has(node)) return;
    seen.add(node);
    if (Array.isArray(node)) { node.forEach(walk); return; }
    const o = node as Record<string, unknown>;
    const names = o.areaNameList;
    const ids = o.areaIdList;
    if (Array.isArray(names) && Array.isArray(ids)) {
      const f = o.floor === undefined || o.floor === null ? undefined : String(o.floor);
      if (!floor || !f || f === floor) {
        for (let i = 0; i < Math.min(names.length, ids.length); i++) {
          out.push({ id: String(ids[i]), name: String(names[i]), floor: f });
        }
      }
    }
    Object.values(o).forEach(walk);
  };
  walk(data);
  // The same area is listed once per robot that knows it, so dedupe by id.
  const byId = new Map(out.map((a) => [a.id, a]));
  return [...byId.values()];
}
